import type { MrpIncomingRow, MrpInventoryRow } from "@/lib/mrp";

import {
  type KittingCartSlot,
  incomingRowsEligibleForKitting,
  kittingAppendEntryLocationToSlotKey,
  kittingEntryLocationFromIncomingRow,
  kittingIncomingDemandQuantity,
  kittingPreferredBatchId,
  workOrderIncomingGroupKey,
} from "./kittingCartModel";

export const KITTING_EMPTY_SUBLOCATION_STOP = "No sub location";

/** Hub `mrp.inventory_sublocation` row used for kitting pick qty-on-hand per stop. */
export interface KittingHubSublocationBalance {
  /** Empty string is the canonical key for {@link KITTING_EMPTY_SUBLOCATION_STOP}. */
  sublocation: string;
  quantity: number;
}

export type KittingPickJobKind = "batch" | "work_order" | "transfer_order";

export interface KittingPickJobRef {
  kind: KittingPickJobKind;
  /** Primary floor label: batch id, work order number, or transfer number. */
  label: string;
  batchId?: number;
  workOrderId?: number;
  workOrderNumber?: string;
  workOrderMvId?: number;
  workOrderMvNumber?: string | null;
  transferOrderId?: number;
  transferOrderItemId?: number;
  transferNumber?: string | null;
}

export interface KittingPickLine {
  id: string;
  storageLocation: string;
  sku: string;
  description: string;
  /** Total quantity required on this job line (BOM ordered / transfer line qty). */
  qtyNeeded: number;
  /** Quantity already allocated or packed on this job line before this pick. */
  qtyAllocated: number;
  /** Quantity still due on this job line (incoming `quantity_remaining`). */
  qtyDue: number;
  /** Megaventory StockPhysical at this SubLocation on the latest snapshot. */
  qtyOnHandHere: number;
  destinationSlotId: string;
  destinationLabel: string;
  job: KittingPickJobRef;
  shortageNote: string | null;
}

export function formatKittingDestinationLabel(
  slot: Pick<KittingCartSlot, "headlineLabel" | "deliveryStationName">,
): string {
  return slot.headlineLabel;
}

/** Floor route ordered by storage location (Megaventory SubLocation strings). */
export function buildPickRouteAlphabetical(
  lines: KittingPickLine[],
): { storageLocation: string; lines: KittingPickLine[] }[] {
  const byLoc = new Map<string, KittingPickLine[]>();
  for (const line of lines) {
    const list = byLoc.get(line.storageLocation) ?? [];
    list.push(line);
    byLoc.set(line.storageLocation, list);
  }
  return Array.from(byLoc.entries())
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map(([storageLocation, locLines]) => ({ storageLocation, lines: locLines }));
}

function sublocationStopLabel(raw: string | null | undefined): string {
  const trimmed = raw != null ? String(raw).trim() : "";
  return trimmed.length > 0 ? trimmed : KITTING_EMPTY_SUBLOCATION_STOP;
}

/** Cart bin key for an incoming demand row (batch-first when any batch is linked), split by kit destination. */
export function destinationSlotIdForIncomingRow(
  row: MrpIncomingRow,
  options?: { includeFullyAllocated?: boolean },
): string | null {
  if (row.location_code !== "sgs") return null;
  const { stationId } = kittingEntryLocationFromIncomingRow(row);
  if (row.kind === "transfer_order") {
    if (row.transfer_order_id == null) return null;
    if (!options?.includeFullyAllocated && kittingIncomingDemandQuantity(row) <= 0) return null;
    return `tr-${row.transfer_order_id}`;
  }
  if (row.kind !== "work_order") return null;
  if (!options?.includeFullyAllocated && kittingIncomingDemandQuantity(row) <= 0) return null;
  const preferredBatchId = kittingPreferredBatchId(row.batch_ids);
  if (preferredBatchId != null) {
    return kittingAppendEntryLocationToSlotKey(`batch-${preferredBatchId}`, stationId);
  }
  return kittingAppendEntryLocationToSlotKey(`wo-${workOrderIncomingGroupKey(row)}`, stationId);
}

function workOrderLabelForRow(row: MrpIncomingRow): string {
  const numberPart = row.po_number != null ? row.po_number.trim() : "";
  if (numberPart.length > 0) return numberPart;
  if (row.work_order_mv_id != null) return `MV ${row.work_order_mv_id}`;
  return "—";
}

function transferLabelForRow(row: MrpIncomingRow): string {
  const transferNumber = row.transfer_number?.trim() || (row.transfer_order_id != null ? `T-${row.transfer_order_id}` : "");
  if (transferNumber.length === 0) return "—";
  return transferNumber.match(/^T-/i) ? transferNumber : `T-${transferNumber}`;
}

export function kittingPickJobRefForIncomingRow(row: MrpIncomingRow): KittingPickJobRef {
  if (row.kind === "transfer_order") {
    return {
      kind: "transfer_order",
      label: transferLabelForRow(row),
      transferOrderId: row.transfer_order_id ?? undefined,
      transferOrderItemId: Number.isFinite(Number(row.id)) ? Number(row.id) : undefined,
      transferNumber: row.transfer_number ?? null,
    };
  }
  const workOrderNumber = workOrderLabelForRow(row);
  const preferredBatchId = kittingPreferredBatchId(row.batch_ids);
  if (preferredBatchId != null) {
    return {
      kind: "batch",
      label: String(preferredBatchId),
      batchId: preferredBatchId,
      workOrderId: row.work_order_id ?? undefined,
      workOrderNumber: workOrderNumber !== "—" ? workOrderNumber : undefined,
      workOrderMvId: row.work_order_mv_id ?? undefined,
      workOrderMvNumber: row.work_order_mv_number ?? null,
    };
  }
  return {
    kind: "work_order",
    label: workOrderNumber,
    workOrderId: row.work_order_id ?? undefined,
    workOrderNumber: workOrderNumber !== "—" ? workOrderNumber : undefined,
    workOrderMvId: row.work_order_mv_id ?? undefined,
    workOrderMvNumber: row.work_order_mv_number ?? null,
  };
}

function inventoryRowsByCpn(rows: MrpInventoryRow[]): Map<string, MrpInventoryRow[]> {
  const map = new Map<string, MrpInventoryRow[]>();
  for (const row of rows) {
    if (row.location_code !== "sgs") continue;
    const cpn = String(row.cpn ?? "").trim();
    if (cpn.length === 0) continue;
    const list = map.get(cpn) ?? [];
    list.push(row);
    map.set(cpn, list);
  }
  return map;
}

function physicalAtRow(row: MrpInventoryRow): number {
  const physical = Math.round(Number(row.stock_physical));
  return Number.isFinite(physical) && physical > 0 ? physical : 0;
}

/** Derive needed / allocated / due from an incoming feed row (WO BOM or transfer line). */
export function kittingPickQuantitiesFromIncomingRow(row: MrpIncomingRow): {
  qtyNeeded: number;
  qtyAllocated: number;
  qtyDue: number;
} {
  const qtyDue = kittingIncomingDemandQuantity(row);
  if (row.kind === "work_order") {
    const orderedRaw = row.quantity_ordered;
    const allocatedRaw = row.allocated_stock;
    const ordered =
      orderedRaw != null && Number.isFinite(Number(orderedRaw))
        ? Math.max(0, Math.round(Number(orderedRaw)))
        : qtyDue;
    const allocated =
      allocatedRaw != null && Number.isFinite(Number(allocatedRaw))
        ? Math.max(0, Math.round(Number(allocatedRaw)))
        : Math.max(0, ordered - qtyDue);
    const qtyNeeded = Math.max(ordered, qtyDue + allocated);
    return { qtyNeeded, qtyAllocated: allocated, qtyDue };
  }
  return { qtyNeeded: qtyDue, qtyAllocated: 0, qtyDue };
}

/** Durable pick-run row used to overlay transfer Need / Alloc / Left (incoming remaining does not move). */
export interface KittingPickRunQuantityItem {
  slotKey: string;
  componentSku: string;
  pickedQuantity: number;
  transferOrderId: number | null;
}

function kittingTransferPickRunQuantityKey(
  slotKey: string,
  transferOrderId: number,
  sku: string,
): string {
  return `${slotKey.trim()}\0${String(transferOrderId)}\0${sku.trim().toLowerCase()}`;
}

/** Postgres integer and bigint ids can arrive as strings. Number.isFinite rejects those. */
function kittingTransferOrderId(value: unknown): number | null {
  const id = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(id) || id <= 0) return null;
  return Math.trunc(id);
}

/**
 * Transfer picks live on the pick run, not on incoming remaining. Overlay cart quantity onto
 * Need / Alloc / Left so the job-line strip updates when a SKU is put in a cart bin.
 */
export function applyKittingPickRunQuantitiesToLines(
  lines: readonly KittingPickLine[],
  runItems: readonly KittingPickRunQuantityItem[],
): KittingPickLine[] {
  if (lines.length === 0 || runItems.length === 0) {
    return [...lines];
  }

  const pickedByKey = new Map<string, number>();
  for (const item of runItems) {
    const transferOrderId = kittingTransferOrderId(item.transferOrderId);
    if (transferOrderId == null) continue;
    const quantity = Math.max(0, Math.trunc(Number(item.pickedQuantity)));
    if (quantity <= 0) continue;
    const key = kittingTransferPickRunQuantityKey(item.slotKey, transferOrderId, item.componentSku);
    pickedByKey.set(key, (pickedByKey.get(key) ?? 0) + quantity);
  }
  if (pickedByKey.size === 0) {
    return [...lines];
  }

  return lines.map((line) => {
    if (line.job.kind !== "transfer_order") return line;
    const transferOrderId = kittingTransferOrderId(line.job.transferOrderId);
    if (transferOrderId == null) return line;
    const picked = pickedByKey.get(
      kittingTransferPickRunQuantityKey(line.destinationSlotId, transferOrderId, line.sku),
    );
    if (picked == null || picked <= 0) return line;
    return {
      ...line,
      qtyAllocated: picked,
      qtyDue: Math.max(0, line.qtyNeeded - picked),
    };
  });
}

function kittingPickSkuLocationKey(line: KittingPickLine): string {
  return `${line.sku.trim().toLowerCase()}\0${line.storageLocation}`;
}

/**
 * Lines for the pick route.
 * A full bin stays when another bin for the same SKU at that stop still needs a pick.
 * When every bin at the stop is full, the SKU leaves the route.
 */
export function kittingPickLinesStillToPick(lines: readonly KittingPickLine[]): KittingPickLine[] {
  const openStops = new Set<string>();
  for (const line of lines) {
    if (line.qtyDue > 0) openStops.add(kittingPickSkuLocationKey(line));
  }
  return lines.filter((line) => openStops.has(kittingPickSkuLocationKey(line)));
}

function shortageNoteForDemand(
  _cpn: string,
  qtyDue: number,
  inventoryForCpn: MrpInventoryRow[],
  hubRows?: KittingHubSublocationBalance[],
): string | null {
  if (hubRows != null && hubRows.length > 0) {
    const totalHub = hubRows.reduce(
      (sum, row) => sum + (Number.isFinite(row.quantity) && row.quantity > 0 ? Math.round(row.quantity) : 0),
      0,
    );
    if (totalHub < qtyDue) {
      return `Short: ${totalHub} on hand at SGS across ${hubRows.length} hub sublocation${hubRows.length === 1 ? "" : "s"}; ${qtyDue} due for this job.`;
    }
    return null;
  }
  const totalPhysical = inventoryForCpn.reduce((sum, row) => sum + physicalAtRow(row), 0);
  if (inventoryForCpn.length === 0) {
    return "No SGS row in the latest inventory snapshot for this SKU.";
  }
  if (totalPhysical < qtyDue) {
    return `Short: ${totalPhysical} on hand at SGS across ${inventoryForCpn.length} snapshot row${inventoryForCpn.length === 1 ? "" : "s"}; ${qtyDue} due for this job.`;
  }
  return null;
}

export function buildKittingPickLines(params: {
  incomingRows: MrpIncomingRow[];
  inventoryRows: MrpInventoryRow[];
  /** When present for a CPN, hub sublocation qty drives pick stops instead of snapshot fiction. */
  hubSublocationsByCpn?: Map<string, KittingHubSublocationBalance[]>;
  cartSlotById: Map<string, KittingCartSlot>;
  displayNameForCpn: (cpn: string) => string;
}): KittingPickLine[] {
  const { incomingRows, inventoryRows, hubSublocationsByCpn, cartSlotById, displayNameForCpn } = params;
  const eligible = incomingRowsEligibleForKitting(incomingRows);
  const inventoryByCpn = inventoryRowsByCpn(inventoryRows);
  const lines: KittingPickLine[] = [];

  for (const row of eligible) {
    const destinationSlotId = destinationSlotIdForIncomingRow(row);
    if (destinationSlotId == null) continue;
    const slot = cartSlotById.get(destinationSlotId);
    if (slot == null) continue;

    const cpn = String(row.cpn ?? "").trim();
    if (cpn.length === 0) continue;

    const { qtyNeeded, qtyAllocated, qtyDue } = kittingPickQuantitiesFromIncomingRow(row);
    if (qtyDue <= 0) continue;

    const job = kittingPickJobRefForIncomingRow(row);
    const destinationLabel = formatKittingDestinationLabel(slot);
    const inventoryForCpn = inventoryByCpn.get(cpn) ?? [];
    const hubRows = hubSublocationsByCpn?.get(cpn);
    const useHubSublocations = hubRows != null && hubRows.length > 0;
    const shortageBase = shortageNoteForDemand(
      cpn,
      qtyDue,
      inventoryForCpn,
      useHubSublocations ? hubRows : undefined,
    );

    if (useHubSublocations) {
      const withStock = hubRows.filter((row) => row.quantity > 0);
      const hubStops = withStock.length > 0 ? withStock : hubRows;
      for (const hubRow of hubStops) {
        const storageLocation = sublocationStopLabel(hubRow.sublocation);
        const qtyOnHandHere =
          Number.isFinite(hubRow.quantity) && hubRow.quantity > 0
            ? Math.round(hubRow.quantity)
            : 0;
        lines.push({
          id: `${row.id}::${destinationSlotId}::${storageLocation}`,
          storageLocation,
          sku: cpn,
          description: displayNameForCpn(cpn),
          qtyNeeded,
          qtyAllocated,
          qtyDue,
          qtyOnHandHere,
          destinationSlotId,
          destinationLabel,
          job,
          shortageNote: shortageBase,
        });
      }
      continue;
    }

    const withStock = inventoryForCpn.filter((inv) => physicalAtRow(inv) > 0);
    const stops =
      withStock.length > 0
        ? withStock
        : inventoryForCpn.length > 0
          ? inventoryForCpn
          : [null];

    for (const inv of stops) {
      const storageLocation =
        inv == null ? KITTING_EMPTY_SUBLOCATION_STOP : sublocationStopLabel(inv.sublocation);
      const qtyOnHandHere = inv == null ? 0 : physicalAtRow(inv);
      const invNotes =
        inv?.notes != null && String(inv.notes).trim() !== "" ? String(inv.notes).trim() : null;
      const shortageNote =
        invNotes != null && shortageBase != null
          ? `${shortageBase} ${invNotes}`
          : (shortageBase ?? invNotes);

      lines.push({
        id: `${row.id}::${destinationSlotId}::${storageLocation}`,
        storageLocation,
        sku: cpn,
        description: displayNameForCpn(cpn),
        qtyNeeded,
        qtyAllocated,
        qtyDue,
        qtyOnHandHere,
        destinationSlotId,
        destinationLabel,
        job,
        shortageNote,
      });
    }
  }

  lines.sort((left, right) => {
    const loc = left.storageLocation.localeCompare(right.storageLocation, undefined, { sensitivity: "base" });
    if (loc !== 0) return loc;
    const sku = left.sku.localeCompare(right.sku, undefined, { sensitivity: "base" });
    if (sku !== 0) return sku;
    return left.destinationLabel.localeCompare(right.destinationLabel, undefined, { sensitivity: "base" });
  });

  return lines;
}

export function matchesKittingPickSearch(line: KittingPickLine, query: string): boolean {
  if (query.trim().length === 0) return true;
  const q = query.trim().toLowerCase();
  const hay = [
    line.storageLocation,
    line.sku,
    line.description,
    line.job.label,
    line.job.workOrderNumber,
    line.job.label,
    line.destinationLabel,
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}
